import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Layout from "@/components/Layout";
import { FadeUp } from "@/components/Motion";
import {
  ArrowRight, ArrowLeft, Check, Hammer, Building2, Megaphone, Printer, PartyPopper,
  Cpu, Server, Armchair, Hotel, Briefcase, Users, Calculator, Scale, Plane, Search,
  Mail, Calendar, FileText, Sparkles, X, ShieldCheck, Heart,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const ICONS = { Hammer, Building2, Megaphone, Printer, PartyPopper, Cpu, Server, Armchair, Hotel, Briefcase, Users, Calculator, Scale, Plane };

function CategoryIcon({ name, size = 18, className = "" }) {
  const I = ICONS[name] || Briefcase;
  return <I size={size} className={className} strokeWidth={1.4} />;
}

export default function Opportunities() {
  const { slug } = useParams();
  if (slug) return <CategoryDetail slug={slug} />;
  return <CategoryGrid />;
}

function CategoryGrid() {
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/procurement/categories").then(({ data }) => setCats(data.categories || []));
  }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return cats;
    const needle = q.toLowerCase();
    return cats.filter((c) =>
      (c.name + " " + (c.tagline || "") + " " + (c.overview || "")).toLowerCase().includes(needle),
    );
  }, [cats, q]);

  return (
    <Layout>
      {/* Hero */}
      <section className="border-b border-[hsl(var(--line))] bg-[#0A0A0A] text-white">
        <div className="container-summit py-16 md:py-24">
          <FadeUp className="overline text-zinc-400 mb-6">
            <span className="accent-rule" />Travel + Leisure · Procurement
          </FadeUp>
          <FadeUp delay={0.05}>
            <h1 className="serif text-5xl sm:text-7xl leading-[1.02] max-w-4xl">
              Procurement <span className="text-[#A39171]">Opportunities</span>
            </h1>
          </FadeUp>
          <FadeUp delay={0.1}>
            <p className="mt-8 max-w-2xl text-zinc-300 text-base sm:text-lg leading-relaxed">
              The destination for strategic suppliers. Each category surfaces Travel + Leisure's
              sourcing priorities, current opportunities, qualifications, and the leader behind it.
            </p>
          </FadeUp>
          <FadeUp delay={0.15}>
            <div className="mt-10 flex flex-wrap gap-8 text-xs uppercase tracking-[0.22em] text-zinc-400">
              <Stat label="Strategic Categories" value={cats.length} />
              <Stat label="Annual Sourcing Cycles" value="4" />
              <Stat label="Diverse Supplier Goal" value="25%" />
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Search */}
      <section className="border-b border-[hsl(var(--line))] bg-white sticky top-16 sm:top-20 z-20">
        <div className="container-summit py-5 flex items-center gap-4">
          <Search size={16} className="text-zinc-400 shrink-0" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search categories, capabilities, sourcing priorities…"
            className="flex-1 text-sm bg-transparent outline-none placeholder:text-zinc-400 tracking-tight"
            data-testid="opp-search"
          />
          <span className="text-xs uppercase tracking-[0.22em] text-zinc-400">
            {filtered.length} / {cats.length}
          </span>
        </div>
      </section>

      {/* Grid */}
      <section className="container-summit py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[hsl(var(--line))]">
          {filtered.map((c, i) => (
            <motion.div
              key={c.slug}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: Math.min(0.3, i * 0.04) }}
            >
              <Link
                to={`/opportunities/${c.slug}`}
                data-testid={`opp-card-${c.slug}`}
                className="group relative bg-white p-8 h-full flex flex-col gap-5 hover:bg-zinc-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <CategoryIcon name={c.icon} size={22} className="text-[#A39171]" />
                  <ArrowRight size={14} className="text-zinc-300 group-hover:text-black transition-colors group-hover:translate-x-1" />
                </div>
                <div>
                  <div className="overline text-zinc-500">{c.tagline}</div>
                  <h3 className="serif text-2xl mt-2">{c.name}</h3>
                </div>
                <p className="text-sm text-zinc-600 line-clamp-3 leading-relaxed">{c.overview}</p>
                <div className="mt-auto pt-4 border-t border-zinc-100 flex items-center justify-between text-xs uppercase tracking-[0.18em] text-zinc-500">
                  <span>{c.spend_band || "—"}</span>
                  <span className="tabular-nums">{c.supplier_count_est || 0} partners</span>
                </div>
              </Link>
            </motion.div>
          ))}
          {filtered.length === 0 && (
            <div className="bg-white p-12 col-span-full text-center text-sm text-zinc-500">
              No categories match — try a different search term.
            </div>
          )}
        </div>
      </section>

      {/* Strategic note */}
      <section className="border-t border-[hsl(var(--line))] bg-[#FAFAF7]">
        <div className="container-summit py-16 grid md:grid-cols-2 gap-12">
          <FadeUp>
            <div className="overline mb-4"><span className="accent-rule" />For Strategic Suppliers</div>
            <h2 className="serif text-3xl sm:text-4xl">Built for procurement-grade engagement.</h2>
            <p className="mt-6 text-zinc-600 leading-relaxed max-w-xl">
              Express interest in seconds. Request a formal introduction when you're ready to do
              business. Every conversation is routed to the Travel + Leisure category owner.
            </p>
          </FadeUp>
          <FadeUp delay={0.05} className="grid sm:grid-cols-2 gap-px bg-[hsl(var(--line))]">
            <Pillar icon={Heart} title="One-click Interest" body="Signal alignment in 1 click — your name and company are passed to the category owner." />
            <Pillar icon={ShieldCheck} title="Vetted Pipeline" body="Every intro request is reviewed by Travel + Leisure procurement leadership." />
            <Pillar icon={Sparkles} title="AI Matchmaking" body="Get a Business Match Score per category in your Procurement page." />
            <Pillar icon={Calendar} title="Important Dates" body="See upcoming RFP windows and qualification deadlines per category." />
          </FadeUp>
        </div>
      </section>
    </Layout>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="serif text-3xl text-white tabular-nums leading-none">{value}</div>
      <div className="mt-2">{label}</div>
    </div>
  );
}

function Pillar({ icon: Icon, title, body }) {
  return (
    <div className="bg-white p-6">
      <Icon size={18} className="text-[#A39171]" strokeWidth={1.4} />
      <div className="serif text-lg mt-4">{title}</div>
      <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{body}</p>
    </div>
  );
}

function CategoryDetail({ slug }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const [cat, setCat] = useState(null);
  const [introOpen, setIntroOpen] = useState(false);
  const [interested, setInterested] = useState(false);
  const [exhibitor, setExhibitor] = useState(null);

  useEffect(() => {
    api.get(`/procurement/categories/${slug}`).then(({ data }) => setCat(data.category)).catch(() => nav("/opportunities"));
    if (user) {
      api.get("/exhibitors/me").then(({ data }) => setExhibitor(data.exhibitor)).catch(() => {});
    }
  }, [slug, user, nav]);

  const expressInterest = async () => {
    try {
      const payload = {
        company_name: exhibitor?.company_name || "",
        contact_name: exhibitor?.primary_contact || user?.name || "",
      };
      const email = exhibitor?.email || user?.email;
      if (email) payload.email = email;
      if (exhibitor?.id) payload.exhibitor_id = exhibitor.id;
      await api.post(`/procurement/categories/${slug}/interest`, payload);
      setInterested(true);
      toast.success("Interest registered. The category owner has been notified.");
    } catch (e) {
      toast.error("Could not register interest. Please try again.");
    }
  };

  if (!cat) {
    return (
      <Layout>
        <section className="container-summit py-24"><div className="overline">Loading category…</div></section>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero */}
      <section className="border-b border-[hsl(var(--line))] bg-[#0A0A0A] text-white">
        <div className="container-summit py-12 md:py-16">
          <Link to="/opportunities" data-testid="opp-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-zinc-400 hover:text-white">
            <ArrowLeft size={14} /> All Categories
          </Link>
          <div className="mt-8 grid md:grid-cols-[auto_1fr] gap-8 items-start">
            <div className="h-16 w-16 border border-white/15 flex items-center justify-center">
              <CategoryIcon name={cat.icon} size={28} className="text-[#A39171]" />
            </div>
            <div>
              <div className="overline text-zinc-400">{cat.tagline}</div>
              <h1 className="serif text-5xl sm:text-7xl mt-3 leading-[1.02]">{cat.name}</h1>
              <p className="mt-6 max-w-3xl text-zinc-300 text-base sm:text-lg leading-relaxed">
                {cat.overview}
              </p>
              <div className="mt-8 flex flex-wrap gap-x-10 gap-y-3 text-xs uppercase tracking-[0.22em] text-zinc-400">
                <div><span className="serif text-2xl text-white tabular-nums block">{cat.spend_band || "—"}</span>Annual Spend Band</div>
                <div><span className="serif text-2xl text-white tabular-nums block">{cat.supplier_count_est || 0}</span>Active Partners</div>
                <div><span className="serif text-2xl text-white tabular-nums block">{(cat.upcoming_opportunities || []).length}</span>Live Opportunities</div>
              </div>
            </div>
          </div>

          {/* CTA bar */}
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <button
              onClick={expressInterest}
              disabled={interested}
              data-testid="opp-interest-btn"
              className={`inline-flex items-center gap-3 h-12 px-7 text-xs uppercase tracking-[0.22em] transition-colors ${
                interested ? "bg-[#A39171] text-white" : "bg-white text-black hover:bg-[#A39171] hover:text-white"
              }`}
            >
              {interested ? (<><Check size={14} /> Interest Registered</>) : (<><Heart size={14} /> I'm Interested</>)}
            </button>
            <button
              onClick={() => setIntroOpen(true)}
              data-testid="opp-intro-btn"
              className="inline-flex items-center gap-3 h-12 px-7 text-xs uppercase tracking-[0.22em] border border-white/40 text-white hover:bg-white hover:text-black transition-colors"
            >
              <Mail size={14} /> Request Introduction
            </button>
          </div>
        </div>
      </section>

      {/* Detail grid */}
      <section className="container-summit py-12 md:py-16 grid lg:grid-cols-3 gap-px bg-[hsl(var(--line))]">
        <Section title="Current Sourcing Priorities" icon={Sparkles} items={cat.sourcing_priorities} />
        <Section title="Upcoming Opportunities" icon={Calendar} items={cat.upcoming_opportunities} />
        <Section title="Preferred Supplier Qualifications" icon={ShieldCheck} items={cat.qualifications} />
      </section>

      {/* Owner + Dates + Docs */}
      <section className="container-summit pb-16 grid lg:grid-cols-3 gap-px bg-[hsl(var(--line))]">
        <FadeUp className="bg-white p-8 flex flex-col gap-4">
          <div className="overline">Category Owner</div>
          <Users size={20} className="text-[#A39171]" strokeWidth={1.4} />
          {cat.category_owner ? (
            <>
              <div className="serif text-2xl">{cat.category_owner.name}</div>
              <div className="overline">{cat.category_owner.title}</div>
              {cat.category_owner.email && (
                <a href={`mailto:${cat.category_owner.email}`} className="text-sm text-zinc-700 hover:text-black inline-flex items-center gap-2">
                  <Mail size={14} /> {cat.category_owner.email}
                </a>
              )}
            </>
          ) : (
            <div className="text-sm text-zinc-500">Owner to be assigned.</div>
          )}
        </FadeUp>

        <FadeUp delay={0.05} className="bg-white p-8 flex flex-col gap-4">
          <div className="overline">Important Dates</div>
          <Calendar size={20} className="text-[#A39171]" strokeWidth={1.4} />
          {(cat.important_dates || []).length === 0 ? (
            <div className="text-sm text-zinc-500">Schedule announcements will appear here.</div>
          ) : (
            <ul className="space-y-3">
              {cat.important_dates.map((d, i) => (
                <li key={i} className="flex items-baseline justify-between text-sm gap-4">
                  <span className="text-zinc-700">{d.label}</span>
                  <span className="overline whitespace-nowrap">{d.date}</span>
                </li>
              ))}
            </ul>
          )}
        </FadeUp>

        <FadeUp delay={0.1} className="bg-white p-8 flex flex-col gap-4">
          <div className="overline">Documents</div>
          <FileText size={20} className="text-[#A39171]" strokeWidth={1.4} />
          {(cat.document_ids || []).length === 0 ? (
            <div className="text-sm text-zinc-500">
              Category one-pagers and RFP overviews will be published here closer to each sourcing window.
            </div>
          ) : (
            <ul className="space-y-2">
              {cat.document_ids.map((id) => (
                <li key={id}>
                  <a href={`/api/files/${id}`} target="_blank" rel="noopener noreferrer" className="text-sm text-zinc-700 hover:text-black inline-flex items-center gap-2">
                    <FileText size={14} /> Document
                  </a>
                </li>
              ))}
            </ul>
          )}
        </FadeUp>
      </section>

      {/* Intro Request Modal */}
      <Dialog open={introOpen} onOpenChange={setIntroOpen}>
        <DialogContent className="max-w-2xl rounded-none p-0 border-0">
          <IntroForm
            cat={cat}
            exhibitor={exhibitor}
            onClose={() => setIntroOpen(false)}
            onSubmitted={() => { setIntroOpen(false); toast.success("Your introduction request has been sent to procurement."); }}
          />
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function Section({ title, icon: Icon, items }) {
  return (
    <FadeUp className="bg-white p-8">
      <div className="flex items-center justify-between">
        <div className="overline">{title}</div>
        <Icon size={18} className="text-[#A39171]" strokeWidth={1.4} />
      </div>
      <ul className="mt-6 space-y-4">
        {(items || []).length === 0 && <li className="text-sm text-zinc-500">Coming soon.</li>}
        {(items || []).map((it, i) => (
          <li key={i} className="flex items-start gap-3 text-sm text-zinc-700 leading-relaxed">
            <span className="mt-2 h-px w-4 bg-[#A39171] shrink-0" />
            {it}
          </li>
        ))}
      </ul>
    </FadeUp>
  );
}

function IntroForm({ cat, exhibitor, onClose, onSubmitted }) {
  const [form, setForm] = useState({
    company_name: exhibitor?.company_name || "",
    contact_name: exhibitor?.primary_contact || "",
    email: exhibitor?.email || "",
    phone: exhibitor?.phone || "",
    website: exhibitor?.website || "",
    capabilities: "",
    preferred_meeting_window: "",
    additional_notes: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/procurement/categories/${cat.slug}/intro-request`, {
        ...form,
        exhibitor_id: exhibitor?.id,
      });
      onSubmitted();
    } catch (err) {
      toast.error("Could not send. Please check the required fields.");
    } finally {
      setBusy(false);
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="bg-white">
      <DialogHeader className="px-8 pt-8 pb-0 flex flex-row items-start justify-between gap-4">
        <div>
          <div className="overline">Request Introduction · {cat.name}</div>
          <DialogTitle className="serif text-3xl mt-2 font-normal text-left">Tell us about your business.</DialogTitle>
          <p className="mt-3 text-sm text-zinc-500 max-w-md">
            Your request will be routed directly to{" "}
            <span className="text-black">{cat.category_owner?.name || "the category owner"}</span>
            {" "}and the Travel + Leisure procurement team.
          </p>
        </div>
        <button onClick={onClose} className="text-zinc-400 hover:text-black" aria-label="close" data-testid="intro-close">
          <X size={18} />
        </button>
      </DialogHeader>

      <form onSubmit={submit} className="px-8 pb-8 pt-6 grid gap-5">
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Company">
            <Input required value={form.company_name} onChange={(e) => set("company_name", e.target.value)} className="h-12 rounded-none" data-testid="intro-company" />
          </Field>
          <Field label="Primary Contact">
            <Input required value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} className="h-12 rounded-none" data-testid="intro-contact" />
          </Field>
          <Field label="Email">
            <Input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="h-12 rounded-none" data-testid="intro-email" />
          </Field>
          <Field label="Phone (optional)">
            <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="h-12 rounded-none" />
          </Field>
          <Field label="Website (optional)" className="sm:col-span-2">
            <Input value={form.website} onChange={(e) => set("website", e.target.value)} className="h-12 rounded-none" placeholder="https://…" />
          </Field>
        </div>
        <Field label="Relevant Capabilities (required)">
          <Textarea required rows={4} value={form.capabilities} onChange={(e) => set("capabilities", e.target.value)} className="rounded-none"
            placeholder="Briefly describe the capability you want to bring to T+L — scope, scale, comparable references." data-testid="intro-capabilities" />
        </Field>
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Preferred Meeting Window">
            <Input value={form.preferred_meeting_window} onChange={(e) => set("preferred_meeting_window", e.target.value)} className="h-12 rounded-none" placeholder="e.g. Within 30 days, Q3, …" />
          </Field>
          <Field label="Additional Notes">
            <Input value={form.additional_notes} onChange={(e) => set("additional_notes", e.target.value)} className="h-12 rounded-none" />
          </Field>
        </div>
        <div className="flex items-center justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="h-12 px-6 text-xs uppercase tracking-[0.22em] text-zinc-500 hover:text-black">
            Cancel
          </button>
          <button type="submit" disabled={busy} className="btn-black h-12 px-8" data-testid="intro-submit">
            {busy ? "Sending…" : "Submit Introduction Request"} <ArrowRight size={14} />
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="overline">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
